import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/Button';
interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  timestamp: string;
}
const defaultConsent: CookieConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
  timestamp: ''
};
const STORAGE_KEY = 'izio_cookie_consent';
function getConsent(): CookieConsent | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}
function setConsentStorage(consent: CookieConsent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Ignore storage errors
  }
}
type View = 'banner' | 'preferences' | 'hidden';
export function CookieBanner() {
  const [view, setView] = useState<View>(() => {
    if (typeof window === 'undefined') return 'hidden';
    return getConsent() ? 'hidden' : 'banner';
  });
  const [openedFrom, setOpenedFrom] = useState<'banner' | 'footer'>('banner');
  const [preferences, setPreferences] = useState<CookieConsent>(defaultConsent);
  useEffect(() => {
    const handler = () => {
      const existing = getConsent();
      setPreferences(existing || {
        ...defaultConsent,
        timestamp: new Date().toISOString()
      });
      setOpenedFrom('footer');
      setView('preferences');
    };
    window.addEventListener('open-cookie-preferences', handler);
    return () => window.removeEventListener('open-cookie-preferences', handler);
  }, []);
  const saveAndClose = useCallback((consent: CookieConsent) => {
    setConsentStorage(consent);
    setView('hidden');
  }, []);
  const handleAcceptAll = useCallback(() => {
    saveAndClose({
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString()
    });
  }, [saveAndClose]);
  const handleRejectAll = useCallback(() => {
    saveAndClose({
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString()
    });
  }, [saveAndClose]);
  const handleSavePreferences = useCallback(() => {
    saveAndClose({
      ...preferences,
      necessary: true,
      timestamp: new Date().toISOString()
    });
  }, [preferences, saveAndClose]);
  if (view === 'hidden') return null;
  return <div className="fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none" data-id="element-2126">
      {view === 'preferences' && <div className="fixed inset-0 bg-black/50 pointer-events-auto" onClick={() => setView(openedFrom === 'footer' ? 'hidden' : 'banner')} data-id="element-2127" />}

      {view === 'banner' && <div className="pointer-events-auto w-full max-w-2xl rounded-2xl border border-dark-4 bg-dark-2 p-6 shadow-2xl" data-id="element-2128">
          <div className="mb-4" data-id="element-2129">
            <h2 className="text-base font-semibold text-light-1" data-id="element-2130">
              Respectam confidentialitatea ta
            </h2>
            <p className="text-sm text-light-3 mt-2 leading-relaxed" data-id="element-2131">
              Folosim cookie-uri pentru a imbunatati experienta pe site.
              Cookie-urile esentiale sunt necesare pentru functionarea
              site-ului. Celelalte cookie-uri ne ajuta sa intelegem cum
              folosesti site-ul si sa iti oferim continut relevant.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-2132">
            <Button onClick={handleRejectAll} variant="outline" className="flex-1 text-sm" data-id="element-2133">
              Refuza toate
            </Button>
            <Button onClick={() => {
          const existing = getConsent();
          setPreferences(existing || {
            ...defaultConsent,
            timestamp: new Date().toISOString()
          });
          setOpenedFrom('banner');
          setView('preferences');
        }} variant="outline" className="flex-1 text-sm" data-id="element-2134">
              Gestioneaza preferintele
            </Button>
            <Button onClick={handleAcceptAll} className="flex-1 text-sm" data-id="element-2135">
              Accepta toate
            </Button>
          </div>
        </div>}

      {view === 'preferences' && <div className="pointer-events-auto w-full max-w-lg rounded-2xl border border-dark-4 bg-dark-2 p-6 shadow-2xl relative z-10 max-h-[80vh] overflow-y-auto" data-id="element-2136">
          <h2 className="text-base font-semibold text-light-1 mb-1" data-id="element-2137">
            Preferinte cookie-uri
          </h2>
          <p className="text-sm text-light-3 mb-6" data-id="element-2138">
            Alege categoriile de cookie-uri pe care le accepti. Cookie-urile
            esentiale nu pot fi dezactivate.
          </p>

          <div className="space-y-4" data-id="element-2139">
            <CategoryToggle label="Esentiale" description="Necesare pentru functionarea site-ului (autentificare, securitate, preferinte de baza)." checked={true} disabled={true} data-id="element-2140" />
            <CategoryToggle label="Analitice" description="Ne ajuta sa intelegem cum este folosit site-ul pentru a-l imbunatati (ex: Google Analytics)." checked={preferences.analytics} onChange={v => setPreferences(p => ({
          ...p,
          analytics: v
        }))} data-id="element-2141" />
            <CategoryToggle label="Marketing" description="Folosite pentru a afisa reclame relevante pe alte site-uri si retele sociale." checked={preferences.marketing} onChange={v => setPreferences(p => ({
          ...p,
          marketing: v
        }))} data-id="element-2142" />
            <CategoryToggle label="Functionale" description="Permit functionalitati suplimentare precum chat, video sau partajare pe retele sociale." checked={preferences.functional} onChange={v => setPreferences(p => ({
          ...p,
          functional: v
        }))} data-id="element-2143" />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6" data-id="element-2144">
            <Button onClick={handleRejectAll} variant="outline" className="flex-1 text-sm" data-id="element-2145">
              Refuza toate
            </Button>
            <Button onClick={handleSavePreferences} className="flex-1 text-sm" data-id="element-2146">
              Salveaza preferintele
            </Button>
          </div>
        </div>}
    </div>;
}
function CategoryToggle({
  label,
  description,
  checked,
  disabled,
  onChange
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
}) {
  return <div className="flex items-start gap-4 p-4 rounded-xl border border-dark-4 bg-dark-1/50" data-id="element-2147">
      <div className="flex-1 min-w-0" data-id="element-2148">
        <p className="text-sm font-medium text-light-1" data-id="element-2149">{label}</p>
        <p className="text-xs text-light-3 mt-0.5" data-id="element-2150">{description}</p>
      </div>
      <button type="button" role="switch" aria-checked={checked} aria-label={`${label} cookie-uri`} disabled={disabled} onClick={() => onChange?.(!checked)} className={`
          relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full
          transition-colors duration-200
          ${checked ? 'bg-primary' : 'bg-dark-4'}
          ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
        `} data-id="element-2151">
        <span className={`
            inline-block h-4 w-4 rounded-full bg-white shadow-sm
            transition-transform duration-200
            ${checked ? 'translate-x-6' : 'translate-x-1'}
          `} data-id="element-2152" />
      </button>
    </div>;
}